import {
  type Coordinate,
  type Track,
  type TrackSessionDto,
  type RawTelemetryData,
  type Device,
  type SessionFeatures,
  PROCESSING_TELEMETRY_TYPES,
  processRawTelemetryWithTrackDto,
  isEmptyGuid,
} from '@pitbrain/shared'
import useApi from '../../api/useApi'
import ConfigurationContext from '../../core/providers/ConfigurationContext'
import { useContext } from 'react'
import { useTrackSessionContext } from '../trackSession/TrackSessionContext'
import { mapToTrackSession } from '../load/mapToTrackSession'

const ON_ANOTHER_TRACK =
  'The uploaded file is from a different track than the session that is already loaded.'

const validateTrackCompatibility = (
  existingTrackId: string | undefined,
  newTrackId: string | undefined
): void => {
  const existingIsUnknown = isEmptyGuid(existingTrackId)
  const newIsUnknown = !newTrackId

  const tracksDiffer =
    existingIsUnknown !== newIsUnknown ||
    (!existingIsUnknown && !newIsUnknown && existingTrackId !== newTrackId)

  if (tracksDiffer) {
    throw Error(ON_ANOTHER_TRACK)
  }
}

export const useBaseProcessor = () => {
  const { setTrackSessions, trackSessions } = useTrackSessionContext()

  const bffConfig = useContext(ConfigurationContext)
  const api = useApi(bffConfig)
  const loadStoredSession = async (
    fileName: string
  ): Promise<TrackSessionDto | null> => {
    const stored = await api.trackSessionApi.getTrackSessionByName(
      fileName,
      PROCESSING_TELEMETRY_TYPES
    )
    if (stored == null) return null

    const track = await api.trackApi.getTrack(stored.trackId)
    if (track == null) return stored
    if (
      trackSessions?.length &&
      trackSessions[0].track?.id !== stored.trackId
    ) {
      throw Error(ON_ANOTHER_TRACK)
    }

    const sharedLaps = await api.trackSessionApi.getSharedLapsForTrackSession(
      stored.id
    )
    const session = await mapToTrackSession(stored, track, sharedLaps ?? [])
    setTrackSessions((prevState) => [...prevState, session])
    return stored
  }

  const getFirstCoordinate = (
    rawTelemetry: RawTelemetryData
  ): Coordinate | null => {
    if (!rawTelemetry.gps || rawTelemetry.gps.length === 0) {
      return null
    }
    const firstPoint = rawTelemetry.gps[0]
    return {
      latitude: firstPoint.latitude,
      longitude: firstPoint.longitude,
    }
  }

  const processAndCreateSession = async (
    rawTelemetry: RawTelemetryData,
    sessionId: string,
    track: Track | null,
    sessionFeatures: SessionFeatures,
    fileName: string,
    device: Device
  ): Promise<void> => {
    if (trackSessions?.length) {
      validateTrackCompatibility(trackSessions[0].track?.id, track?.id)
    }

    const result = await processRawTelemetryWithTrackDto(rawTelemetry, track, {
      sessionId,
      fileName,
      device,
      sessionFeatures,
    })

    setTrackSessions((prevState) => [...prevState, result.trackSession])
  }

  return { loadStoredSession, getFirstCoordinate, processAndCreateSession }
}
